import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal
} from 'react-bootstrap';
import {
  Select,
  DatePicker,
  Spin,
  message,
  Drawer,
  Tag,
  Tooltip,
  Segmented,
  Input,
  Space,
  Empty,
  Alert
} from 'antd';
import dayjs from 'dayjs';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  SearchOutlined,
  FilterOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  RightOutlined,
  BarChartOutlined,
  FileProtectOutlined
} from '@ant-design/icons';
import {
  getConsolidatedReportPreviewDataService,
  downloadConsolidatedReportService,
  getConsolidatedRowDetailService,
  getAllAdminIECsService
} from '../../services/FileDataService';
const { RangePicker } = DatePicker;

// Currency Formatter with USD/INR conversion
const formatCurrency = (val, currency = 'INR', exchangeRate = 85.0) => {
  if (val === null || val === undefined || val === '--') return '--';
  const num = Number(val);
  if (isNaN(num)) return String(val);

  if (currency === 'USD') {
    const usdVal = num / exchangeRate;
    return `$ ${usdVal.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }
  return `₹ ${num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatNumber = (val) => {
  if (val === null || val === undefined || val === '--') return '--';
  const num = Number(val);
  if (isNaN(num)) return String(val);
  return num.toLocaleString('en-IN');
};

const formatPercent = (val) => {
  if (val === null || val === undefined || val === '--') return '--';
  const num = Number(val);
  if (isNaN(num)) return String(val);
  // The backend always sends percentages on a 0–100 scale (e.g. 0.8 means 0.8%),
  // so never rescale small values as if they were fractions.
  return `${num.toFixed(2)}%`;
};

const ConsolidatedReportView = ({ embedded = false, parentIEC = null, parentDates = undefined, parentCurrency = null }) => {
  const navigate = useNavigate();

  // Role & Scope State
  const role = localStorage.getItem('role');
  const initialIEC = (role === 'superadmin' || role === 'group_head') ? (localStorage.getItem('iec_no') || 'ALL') : (localStorage.getItem('iec_no') || '');

  // When embedded, start from the parent's IEC so the first fetch already uses
  // the right scope (avoids a redundant refetch from the prop-sync effect below).
  const [activeIEC, setActiveIEC] = useState(
    (embedded && parentIEC !== null && parentIEC !== undefined) ? parentIEC : initialIEC
  );
  const [iecOptions, setIecOptions] = useState([]);
  const [companyName, setCompanyName] = useState(localStorage.getItem('name') || 'Cummins Generator Technologies India Private Limited');

  // Date Filter State
  const currentYear = dayjs().year();
  const computeInitialDates = (mode) => {
    const yr = dayjs().year();
    if (mode === '5_years') return [dayjs(`${yr - 4}-01-01`), dayjs(`${yr}-12-31`)];
    if (mode === 'current_year') return [dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)];
    if (mode === 'last_year') return [dayjs(`${yr - 1}-01-01`), dayjs(`${yr - 1}-12-31`)];
    if (mode === 'till_date') return null;
    if (mode === 'fy') return [dayjs(`${yr}-04-01`), dayjs(`${yr + 1}-03-31`)];
    if (mode === 'cy') return [dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)];
    return [null, null];
  };

  const computeModeFromDates = (d) => {
    if (!d || (!d[0] && !d[1])) return 'till_date';
    if (d[0] && d[1]) {
      const yr = dayjs().year();
      if (d[0].isSame(dayjs(`${yr}-01-01`), 'day') && d[1].isSame(dayjs(`${yr}-12-31`), 'day')) return 'current_year';
      if (d[0].isSame(dayjs(`${yr - 4}-01-01`), 'day') && d[1].isSame(dayjs(`${yr}-12-31`), 'day')) return '5_years';
      if (d[0].isSame(dayjs(`${yr - 5}-01-01`), 'day') && d[1].isSame(dayjs(`${yr}-12-31`), 'day')) return '5_years';
    }
    return 'custom';
  };

  const initialDateMode = (embedded && parentDates !== undefined)
    ? computeModeFromDates(parentDates)
    : (localStorage.getItem('crd_dateMode') || 'till_date');

  const [dateMode, setDateMode] = useState(initialDateMode);
  const [selectedFYYear, setSelectedFYYear] = useState(dayjs());
  const [selectedCYYear, setSelectedCYYear] = useState(dayjs());
  const [dates, setDates] = useState(() => {
    if (embedded && parentDates !== undefined) {
      return parentDates;
    }
    return computeInitialDates(initialDateMode);
  });

  // UI Settings State
  const [selectedCurrency, setSelectedCurrency] = useState('INR'); // INR | USD
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('crd_activeTab') || 'summary'); // 'summary' | 'standards'

  // Data Loading State
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [summaryRows, setSummaryRows] = useState([]);
  const [standardsRows, setStandardsRows] = useState([]);
  const [userEmail, setUserEmail] = useState('');

  // Sync state when embedded inside alternate dashboard with parent filters
  useEffect(() => {
    if (embedded && parentIEC !== null && parentIEC !== undefined) {
      setActiveIEC(parentIEC);
      if (iecOptions && iecOptions.length > 0) {
        const opt = iecOptions.find(o => o.value === parentIEC);
        if (opt) setCompanyName(opt.fullname);
      }
    }
  }, [embedded, parentIEC, iecOptions]);

  useEffect(() => {
    if (embedded && parentDates !== undefined) {
      setDates(prev => {
        const same = (!prev && !parentDates) || (
          !!prev && !!parentDates &&
          !!prev[0]?.isSame?.(parentDates[0], 'day') &&
          !!prev[1]?.isSame?.(parentDates[1], 'day')
        );
        return same ? prev : parentDates;
      });
      setDateMode(computeModeFromDates(parentDates));
    }
  }, [embedded, parentDates]);

  useEffect(() => {
    if (embedded && parentCurrency) {
      setSelectedCurrency(parentCurrency);
    }
  }, [embedded, parentCurrency]);

  // Drill-Down Drawer State
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillMeta, setDrillMeta] = useState(null); // { title, count, amount, amount_label, columns }
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearchTerm, setDrillSearchTerm] = useState('');
  const [downloadingRowExcel, setDownloadingRowExcel] = useState(false);
  const [expandedRows, setExpandedRows] = useState({ '16': false });

  const toggleRowExpand = (srNo, e) => {
    if (e) e.stopPropagation();
    const key = String(srNo || '');
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Standard Certificates info modal (opened from the Standards tab rows)
  const [stdCertData] = useState({});
  const [showStdModal, setShowStdModal] = useState(false);
  const [modalType, setModalType] = useState('');

  const handleCloseModal = () => {
    setShowStdModal(false);
    setModalType('');
  };

  // The Standards tab (sheet 2) and its rows come straight from the
  // consolidated-report preview response (`res.sheet2` -> standardsRows), so no
  // separate certificate / returns fetch is needed here.

  // Fetch IEC Options for Admins/Group Heads
  useEffect(() => {
    const fetchIECs = async () => {
      if (role === 'superadmin' || role === 'group_head') {
        try {
          const token = localStorage.getItem('token');
          const res = await getAllAdminIECsService(token);
          if (res && res.data && Array.isArray(res.data)) {
            const options = res.data.map(item => ({
              label: `${item.iec_no} - ${item.fullname}`,
              value: item.iec_no,
              fullname: item.fullname
            }));
            options.unshift({ label: 'ALL Companies (Group Consolidated)', value: 'ALL', fullname: 'All Group Companies' });
            setIecOptions(options);
          }
        } catch (e) {
          console.error('Error loading IEC options:', e);
        }
      }
    };
    fetchIECs();
  }, [role]);

  // Handle Date Mode Selector changes
  const handleDateModeChange = (modeVal) => {
    setDateMode(modeVal);
    localStorage.setItem('crd_dateMode', modeVal);
    if (modeVal === '5_years') {
      setDates([dayjs(`${currentYear - 4}-01-01`), dayjs(`${currentYear}-12-31`)]);
    } else if (modeVal === 'current_year') {
      setDates([dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-12-31`)]);
    } else if (modeVal === 'last_year') {
      const ly = currentYear - 1;
      setDates([dayjs(`${ly}-01-01`), dayjs(`${ly}-12-31`)]);
    } else if (modeVal === 'till_date') {
      setDates(null);
    } else if (modeVal === 'fy') {
      const yr = selectedFYYear ? selectedFYYear.year() : currentYear;
      setDates([dayjs(`${yr}-04-01`), dayjs(`${yr + 1}-03-31`)]);
    } else if (modeVal === 'cy') {
      const yr = selectedCYYear ? selectedCYYear.year() : currentYear;
      setDates([dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)]);
    } else if (modeVal === 'custom') {
      if (!dates) {
        setDates([dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-12-31`)]);
      }
    }
  };

  const handleFYYearChange = (val) => {
    const yearObj = val || dayjs();
    setSelectedFYYear(yearObj);
    const yr = yearObj.year();
    setDates([dayjs(`${yr}-04-01`), dayjs(`${yr + 1}-03-31`)]);
  };

  const handleCYYearChange = (val) => {
    const yearObj = val || dayjs();
    setSelectedCYYear(yearObj);
    const yr = yearObj.year();
    setDates([dayjs(`${yr}-01-01`), dayjs(`${yr}-12-31`)]);
  };

  // Fetch Summary Report Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const token = localStorage.getItem('token');
      const fromDateStr = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null;
      const toDateStr = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null;

      const res = await getConsolidatedReportPreviewDataService(token, activeIEC, fromDateStr, toDateStr);
      if (res && res.sheet1 && Array.isArray(res.sheet1)) {
        setSummaryRows(res.sheet1);
      } else {
        setSummaryRows([]);
        setErrorMsg('No summary data available for the selected parameters.');
      }

      if (res && res.sheet2 && Array.isArray(res.sheet2)) {
        setStandardsRows(res.sheet2);
      } else {
        setStandardsRows([]);
      }

      if (res && res.user_email) {
        setUserEmail(res.user_email);
      }
    } catch (err) {
      console.error('Error fetching consolidated report:', err);
      setErrorMsg('Failed to load compliance summary. Please verify server connection.');
    } finally {
      setLoading(false);
    }
  }, [activeIEC, dates]);

  // `fetchData` is recreated whenever activeIEC/dates change. When embedded, the
  // parent can push a new date range one render after this component mounts
  // (its sub-tab date sync), so debounce briefly and skip duplicate signatures —
  // this collapses the mount + prop-sync into a single request with the final range.
  const lastFetchKeyRef = useRef(null);
  useEffect(() => {
    const key = [
      activeIEC,
      dates && dates[0] ? dates[0].format('YYYY-MM-DD') : '',
      dates && dates[1] ? dates[1].format('YYYY-MM-DD') : ''
    ].join('|');
    if (lastFetchKeyRef.current === key) return;
    const id = setTimeout(() => {
      lastFetchKeyRef.current = key;
      fetchData();
    }, embedded ? 120 : 0);
    return () => clearTimeout(id);
  }, [activeIEC, dates, fetchData, embedded]);

  // Handle Full Consolidated Excel Download
  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      message.loading({ content: 'Generating Consolidated Excel Workbook...', key: 'excel_download' });
      const token = localStorage.getItem('token');
      const fromDateStr = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null;
      const toDateStr = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null;

      const response = await downloadConsolidatedReportService(token, activeIEC, fromDateStr, toDateStr);

      if (response && response.data) {
        if (response.data.type === 'application/json') {
          const text = await response.data.text();
          const json = JSON.parse(text);
          message.error({ content: json.message || 'Failed to generate Excel report.', key: 'excel_download' });
          return;
        }

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
        const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
        const filename = `Consolidated_Compliance_Report_${activeIEC}_${dateStr}_${timeStr}.xlsx`;

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success({ content: 'Consolidated Compliance Excel downloaded successfully!', key: 'excel_download' });
      } else {
        message.error({ content: 'Failed to download report file.', key: 'excel_download' });
      }
    } catch (err) {
      console.error('Excel download error:', err);
      message.error({ content: 'Error downloading report file.', key: 'excel_download' });
    } finally {
      setDownloading(false);
    }
  };

  // Map Consolidated Row Name to Report Type Key for Drill-Down Details
  const getReportTypeFromRowName = (rowName) => {
    const name = String(rowName || '').toLowerCase();
    if (name.includes('edpms')) return 'EDPMS';
    if (name.includes('imp pending') || name.includes('summary of imp')) return 'Summary-Imp';
    if (name.includes('exp pending') || name.includes('emp pending') || name.includes('summary of exp') || name.includes('summary of emp')) return 'Summary-Exp';
    if (name.includes('dbk-air scroll') || name.includes('dbk-air net')) return 'DBK-AIR';
    if (name.includes('rodtep scroll') || name.includes('rodtep net')) return 'RODTEP-Exp';
    if (name.includes('rodtep scrip')) return 'Rodtep-Util';
    if (name.includes('igst')) return 'IGST-Refund';
    if (name.includes('advauth')) return 'AdvAuth-EO';
    if (name.includes('epcg')) return 'EPCG-Auth-EO';
    if (name.includes('fta-opportunity') || name.includes('fta-opportunity-i')) return 'FTA-Opportunity';
    if (name.includes('export - above tat')) return 'TAT-ExportClearance';
    if (name.includes('import - above tat') || name.includes('tat')) return 'TAT-ImportClearance';
    if (name.includes('bond')) return 'BOND-Import';
    return null;
  };

  // Select clean preferred columns for drill-down table based on metric name
  const getVisibleColumnsForRow = (rowName, firstItem) => {
    if (!firstItem || typeof firstItem !== 'object') return [];
    const keys = Object.keys(firstItem);
    const name = String(rowName || '').toLowerCase();

    let preferredKeys = [];
    if (name.includes('edpms')) {
      preferredKeys = ['iec_br', 'iec_no', 'sb_no', 'sb_date', 'port_code', 'leo_date', 'days_pending', 'brc_remark', 'remark', 'business_unit', 'plant_name'];
    } else if (name.includes('imp pending') || name.includes('exp pending') || name.includes('emp pending')) {
      preferredKeys = ['iec_no', 'sb_no', 'be_no', 'sb_date', 'be_date', 'port_code', 'leo_date', 'month', 'year', 'docs', 'remark', 'business_unit', 'plant_name'];
    } else if (name.includes('dbk') || name.includes('drawback')) {
      preferredKeys = ['iec_no', 'sb_no', 'sb_date', 'port_code', 'drawback_amt', 'dbk_amount', 'dbk_remark', 'remark', 'business_unit'];
    } else if (name.includes('igst')) {
      preferredKeys = ['iec_no', 'sb_no', 'sb_date', 'port_code', 'igst_amt', 'igst_remark', 'remark', 'business_unit'];
    } else if (name.includes('rodtep')) {
      preferredKeys = ['iec_no', 'sb_no', 'sb_date', 'port_code', 'rodtep_amount', 'rodtep_remark', 'remark', 'business_unit'];
    } else if (name.includes('tat')) {
      preferredKeys = ['iec_no', 'be_no', 'sb_no', 'be_date', 'sb_date', 'port_code', 'daysIgmToOoc', 'daysSubToOoc', 'clearance_days', 'tat', 'remark', 'business_unit', 'plant_name'];
    } else if (name.includes('bond')) {
      preferredKeys = ['iec_no', 'bond_no', 'bond_type', 'valid_days', 'days_to_expiry', 'remark', 'business_unit'];
    }

    const availablePreferred = preferredKeys.filter(k => keys.includes(k));
    if (availablePreferred.length > 0) {
      return availablePreferred;
    }

    const excluded = ['id', 'sbs_id', 'rn', 'brc_utilisation_status', 'created_at', 'updated_at', 'color', 'color_code', 'user_id', 'group_id', 'rn_no'];
    return keys.filter(colKey => {
      const k = colKey.toLowerCase();
      return !excluded.includes(k) && !k.endsWith('_id') && !k.startsWith('id_');
    });
  };

  const drillDateStrs = () => ({
    from: dates && dates[0] ? dates[0].format('YYYY-MM-DD') : null,
    to: dates && dates[1] ? dates[1].format('YYYY-MM-DD') : null,
  });

  // Bond sub-rows (16a, 16b, ...) carry the bond-type name in row.name
  const bondTypeLabel = (row) => (row.isSubRow && /^16[a-z]+$/i.test(String(row.srNo || ''))
    ? String(row.name || '').trim()
    : null);

  // Open the drill-down drawer for a row — uses the dedicated consolidated
  // row-detail endpoint so the records reconcile exactly with the row's count/amount.
  const handleOpenDrillDown = async (row) => {
    setSelectedRow(row);
    setDrawerVisible(true);
    setDrillLoading(true);
    setDrillSearchTerm('');
    setDrillData([]);
    setDrillMeta(null);

    try {
      const { from, to } = drillDateStrs();
      const res = await getConsolidatedRowDetailService(row.srNo, activeIEC, from, to, {
        bondType: bondTypeLabel(row),
      });
      const d = res && res.data ? res.data : {};
      if (d.status === 'error') {
        message.warning(d.message || 'Could not load drill-down for this row.');
        setDrillData([]);
        setDrillMeta(null);
      } else {
        setDrillData(Array.isArray(d.rows) ? d.rows : []);
        setDrillMeta({
          title: d.title,
          count: d.count,
          amount: d.amount,
          amountLabel: d.amount_label,
          columns: Array.isArray(d.columns) ? d.columns : [],
          message: d.message || null,
        });
      }
    } catch (e) {
      console.error('Error fetching row drill-down:', e);
      message.error('Failed to load drill-down details.');
      setDrillData([]);
      setDrillMeta(null);
    } finally {
      setDrillLoading(false);
    }
  };

  // Download the same reconciled records as an Excel workbook.
  const handleDownloadRowExcel = async (row) => {
    try {
      setDownloadingRowExcel(true);
      message.loading({ content: `Preparing ${row.name} Excel...`, key: 'row_excel' });

      const { from, to } = drillDateStrs();
      const res = await getConsolidatedRowDetailService(row.srNo, activeIEC, from, to, {
        bondType: bondTypeLabel(row),
        format: 'excel',
      });
      if (res && res.data) {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const cleanName = String(row.name).trim().replace(/[^a-zA-Z0-9_-]/g, '_');
        link.setAttribute('download', `${cleanName}_Detail.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success({ content: `${row.name} detail downloaded!`, key: 'row_excel' });
      }
    } catch (e) {
      console.error('Row Excel download error:', e);
      message.error({ content: 'Failed to download row detail.', key: 'row_excel' });
    } finally {
      setDownloadingRowExcel(false);
    }
  };

  // Filter Drill Data based on Search — memoized to avoid re-computation on every render
  const filteredDrillData = useMemo(() => drillData.filter(item => {
    if (!drillSearchTerm) return true;
    const term = drillSearchTerm.toLowerCase();
    return Object.values(item).some(val => String(val || '').toLowerCase().includes(term));
  }), [drillData, drillSearchTerm]);



  // Calculate totals for KPI summary tiles
  const totalCount = summaryRows.reduce((acc, r) => acc + (Number(r.count) || 0), 0);
  const totalValue = summaryRows.reduce((acc, r) => acc + (Number(r.valueInINR) || 0), 0);
  const activeCerts = standardsRows.filter(r => String(r.due || '').toLowerCase().includes('active')).length;

  return (
    <div style={{ padding: '16px 20px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>

      {/* Top Header Navigation Bar */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '16px 24px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {!embedded && (
            <Button
              variant="light"
              onClick={() => navigate('/dashboard/alternate-dashboard')}
              style={{ borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 14px' }}
            >
              <ArrowLeftOutlined style={{ fontSize: '12px' }} /> Back to Dashboard
            </Button>
          )}
          <div>
            <h4 style={{ margin: 0, fontWeight: 800, color: '#0f172a', fontSize: '20px', letterSpacing: '-0.02em' }}>
              Consolidated Compliance Dashboard
            </h4>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '3px', fontWeight: 500 }}>
              Company: <b style={{ color: '#0f172a' }}>{companyName}</b> | IEC Scope: <b style={{ color: '#2563eb', backgroundColor: '#eff6ff', padding: '2px 8px', borderRadius: '4px' }}>{activeIEC === 'ALL' ? 'ALL Companies (Group Consolidated)' : activeIEC}</b>
            </div>
          </div>
        </div>

        {/* Global Controls & Download */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>

          {/* Company / IEC Switcher for Standalone View */}
          {!embedded && iecOptions.length > 0 && (
            <Select
              showSearch
              value={activeIEC}
              onChange={(val) => {
                setActiveIEC(val);
                const opt = iecOptions.find(o => o.value === val);
                if (opt) setCompanyName(opt.fullname);
              }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: 240 }}
              placeholder="Search IEC / Company..."
              size="middle"
            >
              {iecOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          )}

          {/* Date Filter & Currency Switcher — only shown on Summary tab */}
          {!embedded && activeTab === 'summary' && (
            <>
              <Select
                value={dateMode}
                onChange={handleDateModeChange}
                style={{ width: 150 }}
                size="middle"
              >
                <Select.Option value="5_years">Past 5 Years</Select.Option>
                <Select.Option value="current_year">Current Year</Select.Option>
                <Select.Option value="last_year">Last Year</Select.Option>
                <Select.Option value="till_date">Till Date (Full History)</Select.Option>
                <Select.Option value="fy">Financial Year</Select.Option>
                <Select.Option value="cy">Calendar Year</Select.Option>
                <Select.Option value="custom">Custom Range</Select.Option>
              </Select>

              {dateMode === 'fy' && (
                <DatePicker
                  picker="year"
                  placeholder="Select FY"
                  value={selectedFYYear}
                  onChange={handleFYYearChange}
                  size="middle"
                  style={{ width: 130 }}
                  format={(val) => (val ? `FY ${val.year()}-${(val.year() + 1).toString().slice(-2)}` : '')}
                />
              )}

              {dateMode === 'cy' && (
                <DatePicker
                  picker="year"
                  placeholder="Select Year"
                  value={selectedCYYear}
                  onChange={handleCYYearChange}
                  size="middle"
                  style={{ width: 130 }}
                  format={(val) => (val ? `${val.year()}` : '')}
                />
              )}

              {dateMode === 'custom' && (
                <RangePicker
                  value={dates}
                  onChange={(val) => setDates(val)}
                  style={{ width: 240 }}
                  size="middle"
                />
              )}

              <Segmented
                size="middle"
                value={selectedCurrency}
                onChange={(val) => setSelectedCurrency(val)}
                options={[
                  { label: '₹ INR', value: 'INR' },
                  { label: '$ USD', value: 'USD' }
                ]}
                style={{ backgroundColor: '#f1f5f9', fontWeight: 700, fontSize: '13px', padding: '2px' }}
              />
            </>
          )}

          {/* Refresh Button */}
          <Button
            variant="light"
            className="cip-crd-action"
            onClick={fetchData}
            disabled={loading}
            style={{ borderRadius: '8px', padding: '6px 14px', fontSize: '13px', height: '38px' }}
            title="Refresh Consolidated Data"
          >
            <ReloadOutlined spin={loading} /> Refresh
          </Button>

          {/* Download Full Excel Button */}
          <Button
            size="sm"
            onClick={handleDownloadExcel}
            disabled={downloading || loading}
            variant="light"
            className="cip-crd-action"
            style={{ borderRadius: '8px', fontSize: '13px', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 18px', height: '38px' }}
          >
            <FileExcelOutlined style={{ fontSize: '15px' }} />
            <span>{downloading ? 'Exporting...' : 'Export Multi-Sheet Excel'}</span>
          </Button>

        </div>
      </div>

      {/* Main Tabbed Executive Summary Table Container */}
      <Card style={{ borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', backgroundColor: '#ffffff', overflow: 'hidden' }}>

        {/* Navigation Tabs */}
        <div style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <Segmented
            size="middle"
            value={activeTab}
            onChange={(val) => { setActiveTab(val); localStorage.setItem('crd_activeTab', val); }}
            options={[
              { label: <span><BarChartOutlined style={{ marginRight: 6 }} />Executive Summary Report (Sheet 1)</span>, value: 'summary' },
              { label: <span><FileProtectOutlined style={{ marginRight: 6 }} />Master Certificates &amp; Compliance (Sheet 2)</span>, value: 'standards' }
            ]}
            style={{ fontWeight: 700, backgroundColor: '#cbd5e1', fontSize: '13.5px', padding: '3px' }}
          />

          <div style={{ fontSize: '13px', color: '#475569', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <EyeOutlined style={{ color: '#2563eb', fontSize: '15px' }} />
            <span>Click on any metric row to view granular line-item transactions</span>
          </div>
        </div>

        {/* Tab 1: Executive Summary Report (Sheet 1) */}
        {activeTab === 'summary' && (
          <div style={{ padding: '0px' }}>
            {loading ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <Spin size="large" tip="Loading Consolidated Compliance Metrics..." />
              </div>
            ) : errorMsg ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Alert
                  message="No Compliance Transactions Found for Selected Date Range"
                  description={
                    <div>
                      <p style={{ margin: '6px 0 14px 0', color: '#475569', fontSize: '13.5px' }}>
                        {errorMsg}
                      </p>
                      <Button
                        type="primary"
                        className="cip-btn-submit"
                        onClick={() => {
                          setDateMode('till_date');
                          setDates(null);
                        }}
                        style={{ fontWeight: 700, borderRadius: '6px' }}
                      >
                        View All-Time Full History (Till Date)
                      </Button>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ maxWidth: '550px', margin: '0 auto', textAlign: 'center', borderRadius: '10px' }}
                />
              </div>
            ) : (
              <>
                <Table responsive hover className="mb-0 align-middle" style={{ borderCollapse: 'collapse', fontSize: '13.5px' }}>
                  <thead style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>
                    <tr style={{ height: '44px' }}>
                      <th style={{ width: '80px', textAlign: 'center', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Sr. No.</th>
                      <th style={{ backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Name of Compliance Metric / Report</th>
                      <th style={{ width: '220px', textAlign: 'right', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Value ({selectedCurrency})</th>
                      <th style={{ width: '150px', textAlign: 'right', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Percentage (%)</th>
                      <th style={{ width: '210px', textAlign: 'right', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Document Count</th>
                      <th style={{ width: '140px', textAlign: 'center', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((row, idx) => {
                      const srNoStr = String(row.srNo || '');
                      const isSub = row.isSubRow || Boolean(srNoStr.match(/^16[a-z]+/i));
                      const isParent = row.isParent || srNoStr === '16';
                      const parentSrNo = isSub ? srNoStr.replace(/[a-z]+$/i, '') : null;

                      // If this is a sub-row and its parent is NOT expanded, do not render it
                      if (isSub && parentSrNo && !expandedRows[parentSrNo]) {
                        return null;
                      }

                      const reportKey = getReportTypeFromRowName(row.name);
                      const hasValue = row.valueInINR !== null && row.valueInINR !== undefined && !isNaN(Number(row.valueInINR));
                      const hasCount = row.count !== null && row.count !== undefined;
                      const isRowExpanded = Boolean(expandedRows[srNoStr]);

                      return (
                        <tr
                          key={idx}
                          onClick={(e) => {
                            if (isParent) {
                              toggleRowExpand(srNoStr, e);
                            } else {
                              handleOpenDrillDown(row);
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: isParent ? '#f1f5f9' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                            borderLeft: isParent ? '4px solid #2563eb' : (isSub ? '4px solid #cbd5e1' : '4px solid transparent'),
                            borderBottom: '1px solid #e2e8f0',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {/* Sr. No. */}
                          <td style={{ textAlign: 'center', fontWeight: 800, color: isParent ? '#0f172a' : '#475569', padding: '12px 16px', fontSize: '13px' }}>
                            {row.srNo}
                          </td>

                          {/* Metric Name */}
                          <td style={{ fontWeight: isParent ? 800 : 600, color: isParent ? '#0f172a' : '#1e293b', padding: isSub ? '12px 16px 12px 36px' : '12px 16px', fontSize: '14px' }}>
                            {isParent && (
                              <span
                                onClick={(e) => toggleRowExpand(srNoStr, e)}
                                title={isRowExpanded ? "Click to collapse sub-rows" : "Click to expand sub-rows"}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '4px',
                                  backgroundColor: isRowExpanded ? '#dbeafe' : '#e2e8f0',
                                  color: isRowExpanded ? '#1d4ed8' : '#475569',
                                  marginRight: '10px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                {isRowExpanded ? <DownOutlined /> : <RightOutlined />}
                              </span>
                            )}
                            {isSub && <span style={{ color: '#2563eb', marginRight: '8px', fontWeight: 700 }}>↳</span>}
                            {row.name}
                          </td>

                          {/* Value */}
                          <td style={{ textAlign: 'right', fontWeight: 700, color: hasValue ? '#16a34a' : '#94a3b8', padding: '12px 16px', fontSize: '14px' }}>
                            {hasValue ? formatCurrency(row.valueInINR, selectedCurrency) : '—'}
                          </td>

                          {/* Percentage */}
                          <td style={{ textAlign: 'right', fontWeight: 600, color: row.percentage ? '#475569' : '#94a3b8', padding: '12px 16px', fontSize: '13.5px' }}>
                            {row.percentage && row.percentage > 0 ? formatPercent(row.percentage) : '—'}
                          </td>

                          {/* Count */}
                          <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                            {hasCount ? (
                              <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#1e293b', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  backgroundColor: isParent ? '#0f172a' : (row.count > 0 ? '#eff6ff' : '#f1f5f9'),
                                  color: isParent ? '#ffffff' : (row.count > 0 ? '#1d4ed8' : '#64748b'),
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '12.5px',
                                  fontWeight: 700,
                                  border: isParent ? 'none' : (row.count > 0 ? '1px solid #bfdbfe' : '1px solid #e2e8f0')
                                }}>
                                  {formatNumber(row.count)}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                            )}
                          </td>

                          {/* Actions */}
                          <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              {reportKey && (
                                <button
                                  type="button"
                                  className="cip-crd-action"
                                  onClick={(e) => { e.stopPropagation(); handleDownloadRowExcel(row); }}
                                  style={{
                                    borderRadius: '6px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    transition: 'all 0.15s ease'
                                  }}
                                  title="Download Granular Excel Report"
                                >
                                  <DownloadOutlined style={{ fontSize: '13px' }} /> Download
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        )}

        {/* Tab 2: Master Certificates & Compliance (Sheet 2) */}
        {activeTab === 'standards' && (
          <div style={{ padding: '0px' }}>
            {loading ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <Spin size="large" tip="Loading Certificates & Return Compliance Data..." />
              </div>
            ) : (
              <Table responsive hover className="mb-0 align-middle" style={{ borderCollapse: 'collapse', fontSize: '13.5px' }}>
                <thead style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                  <tr style={{ height: '44px' }}>
                    <th style={{ width: '90px', textAlign: 'center', backgroundColor: '#1e293b', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Sr. No.</th>
                    <th style={{ backgroundColor: '#1e293b', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Compliance / Certificate Category</th>
                    <th style={{ width: '320px', textAlign: 'center', backgroundColor: '#1e293b', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>File / Certificate No.</th>
                    <th style={{ width: '250px', textAlign: 'center', backgroundColor: '#1e293b', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', textTransform: 'uppercase', padding: '12px 16px', letterSpacing: '0.04em' }}>Validity Status / Due Days</th>
                  </tr>
                </thead>
                <tbody>
                  {standardsRows.map((row, idx) => {
                    const isSub = row.isSub;
                    const isParent = row.isParent;
                    const dueStr = String(row.due || '').toLowerCase();
                    let tagColor = 'blue';
                    if (dueStr.includes('expired')) tagColor = 'error';
                    else if (dueStr.includes('due in') || dueStr.includes('expir')) tagColor = 'warning';
                    else if (dueStr.includes('active')) tagColor = 'success';

                    return (
                      <tr key={idx} style={{ backgroundColor: isSub ? '#f8fafc' : '#ffffff' }}>
                        <td style={{ textAlign: 'center', fontWeight: 800, color: '#334155', padding: '10px 16px', fontSize: '13px' }}>
                          {row.sr}
                        </td>
                        <td style={{ fontWeight: isParent ? 800 : 600, color: isParent ? '#0f172a' : '#334155', padding: '10px 16px', fontSize: '14px' }}>
                          {row.name}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#1e293b', padding: '10px 16px', fontSize: '13.5px' }}>
                          {row.file_no || '--'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '10px 16px' }}>
                          {row.due ? (
                            <Tag color={tagColor} style={{ fontSize: '12px', padding: '4px 12px', fontWeight: 700, borderRadius: '6px' }}>
                              {row.due}
                            </Tag>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '13px' }}>--</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>
        )}

      </Card>

      {/* Interactive Line-Item Drill-Down Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Tag color="geekblue" style={{ fontSize: '13px', padding: '3px 10px', fontWeight: 800, borderRadius: '4px' }}>
                {selectedRow ? `Row ${selectedRow.srNo}` : ''}
              </Tag>
              <span style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                {selectedRow ? selectedRow.name : 'Detailed Drill-Down'}
              </span>
            </div>
            {selectedRow && !drillLoading && drillData.length > 0 && (
              <Button
                size="sm"
                onClick={() => handleDownloadRowExcel(selectedRow)}
                disabled={downloadingRowExcel}
                variant="light"
                className="cip-crd-action"
                style={{ borderRadius: '6px', fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 14px' }}
              >
                <DownloadOutlined style={{ fontSize: '13px' }} /> Export Row (.xlsx)
              </Button>
            )}
          </div>
        }
        placement="right"
        width={940}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        destroyOnClose
        style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}
      >
        <div style={{ padding: '0px' }}>

          {/* Drawer Header Controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' }}>
            <Input
              prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: '14px' }} />}
              placeholder="Search records in this drill-down dataset..."
              value={drillSearchTerm}
              onChange={(e) => setDrillSearchTerm(e.target.value)}
              style={{ width: 320, borderRadius: '8px', fontSize: '13px' }}
              size="middle"
              allowClear
            />
            <div style={{ fontSize: '13px', color: '#475569', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {drillMeta && (() => {
                const headCount = selectedRow && selectedRow.count != null ? Number(selectedRow.count) : null;
                const headAmt = selectedRow && selectedRow.valueInINR != null ? Number(selectedRow.valueInINR) : null;
                const cntOk = headCount == null || headCount === Number(drillMeta.count);
                const amtOk = headAmt == null || drillMeta.amount == null || Math.abs(Number(drillMeta.amount) - headAmt) < 1;
                const fmt = (n) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                return (
                  <>
                    <Tag color={cntOk ? 'green' : 'red'} style={{ fontSize: '12.5px', padding: '3px 10px', fontWeight: 700, borderRadius: '6px' }}>
                      Records: {fmt(drillMeta.count)}{headCount != null ? ` / row ${fmt(headCount)}` : ''} {cntOk ? '✓' : '✕'}
                    </Tag>
                    {drillMeta.amount != null && (
                      <Tag color={amtOk ? 'green' : 'red'} style={{ fontSize: '12.5px', padding: '3px 10px', fontWeight: 700, borderRadius: '6px' }}>
                        {drillMeta.amountLabel || 'Amount'}: ₹{fmt(drillMeta.amount)}{headAmt != null ? ` / row ₹${fmt(headAmt)}` : ''} {amtOk ? '✓' : '✕'}
                      </Tag>
                    )}
                  </>
                );
              })()}
              {drillSearchTerm && <span>Shown: <b style={{ color: '#2563eb', fontSize: '15px' }}>{filteredDrillData.length}</b></span>}
            </div>
          </div>
          {drillMeta && drillMeta.message && (
            <div style={{ marginBottom: '12px', fontSize: '12.5px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px 12px' }}>
              {drillMeta.message}
            </div>
          )}

          {drillLoading ? (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <Spin size="large" tip="Loading Granular Line-Item Transactions..." />
            </div>
          ) : filteredDrillData.length === 0 ? (
            <div style={{ padding: '60px 0', textAlign: 'center' }}>
              <Empty description="No granular transaction records found for this metric." />
            </div>
          ) : (
            <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden', maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
              <Table responsive hover className="mb-0 align-middle" style={{ fontSize: '13px', borderCollapse: 'collapse' }}>
                {(() => {
                  const visibleCols = (drillMeta && drillMeta.columns && drillMeta.columns.length)
                    ? drillMeta.columns
                    : getVisibleColumnsForRow(selectedRow?.name, filteredDrillData[0]);
                  return (
                    <>
                      <thead style={{ backgroundColor: '#0f172a', color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ height: '38px' }}>
                          {visibleCols.map((colKey, cIdx) => (
                            <th key={cIdx} style={{ fontWeight: 800, textTransform: 'capitalize', padding: '10px 12px', fontSize: '12px', backgroundColor: '#0f172a', color: '#ffffff', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>
                              {colKey.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDrillData.map((dRow, rIdx) => {
                          const rawColor = String(dRow.color || dRow.Color || '').toLowerCase();
                          let borderLeftStyle = '4px solid transparent';
                          let rowBg = rIdx % 2 === 0 ? '#ffffff' : '#f8fafc';

                          if (rawColor === 'green') {
                            borderLeftStyle = '4px solid #16a34a';
                            rowBg = '#f0fdf4';
                          } else if (rawColor === 'orange') {
                            borderLeftStyle = '4px solid #f97316';
                            rowBg = '#fff7ed';
                          } else if (rawColor === 'red') {
                            borderLeftStyle = '4px solid #ef4444';
                            rowBg = '#fef2f2';
                          } else if (rawColor === 'black' || rawColor === 'dark') {
                            borderLeftStyle = '4px solid #475569';
                            rowBg = '#f8fafc';
                          }

                          return (
                            <tr
                              key={rIdx}
                              style={{
                                backgroundColor: rowBg,
                                borderLeft: borderLeftStyle,
                                borderBottom: '1px solid #e2e8f0',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              {visibleCols.map((colKey, cIdx) => {
                                let val = dRow[colKey];
                                if (val === null || val === undefined || val === '') val = '—';
                                return (
                                  <td key={cIdx} style={{ padding: '10px 14px', color: '#1e293b', fontSize: '13px', fontWeight: cIdx === 0 ? 600 : 500, whiteSpace: 'nowrap' }}>
                                    {String(val)}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </>
                  );
                })()}
              </Table>
            </div>
          )}

        </div>
      </Drawer>

      {/* Standard Certificate / Return Information Modal */}
      <Modal show={showStdModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0', padding: '16px 24px' }}>
          <Modal.Title style={{ fontWeight: 800, fontSize: '18px', color: '#0f172a' }}>
            {modalType === 'EH' ? 'EH Information' :
              modalType === 'IEC' ? 'IEC Information' :
                modalType === 'AEO' ? 'AEO Information' :
                  modalType === 'Consultation' ? 'Consultation Information' :
                    modalType === 'RETURNS' ? 'Returns Information' :
                      modalType === 'RCMC' ? 'RCMC Information' :
                        modalType === 'DSC' ? 'DSC Information' :
                          `${modalType} Information`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '14.5px', color: '#475569', padding: '24px 28px' }}>
          {modalType === 'IEC' ? (
            (stdCertData['IEC'] && stdCertData['IEC'].length > 0) ? (
              <>
                <p style={{ marginBottom: '10px' }}>IEC: <b style={{ color: '#0f172a' }}>{activeIEC === 'ALL' ? (localStorage.getItem('iec_no') || '') : activeIEC}</b></p>
                <p style={{ marginBottom: '10px' }}>Name: <b style={{ color: '#0f172a' }}>{companyName}</b></p>
                <p style={{ marginBottom: '10px' }}>Expiry Date: {stdCertData['IEC'][0]?.expiry_date || 'N/A'}</p>
                <p style={{ marginBottom: '0px' }}>Status: <b style={{ color: stdCertData['IEC'][0]?.expiry_status?.toLowerCase().includes('no action') || stdCertData['IEC'][0]?.expiry_status?.toLowerCase().includes('active') ? '#16a34a' : '#dc2626' }}>{stdCertData['IEC'][0]?.expiry_status || 'N/A'}</b></p>
              </>
            ) : (
              <>
                <p style={{ marginBottom: '10px' }}>IEC: <b style={{ color: '#0f172a' }}>{activeIEC === 'ALL' ? (localStorage.getItem('iec_no') || '') : activeIEC}</b></p>
                <p style={{ marginBottom: '10px' }}>Name: <b style={{ color: '#0f172a' }}>{companyName}</b></p>
                <p style={{ marginBottom: '0px', color: '#64748b' }}>Expiry details not available in system.</p>
              </>
            )
          ) : (stdCertData[modalType] && stdCertData[modalType].length > 0) ? (
            <>
              <p style={{ marginBottom: '10px' }}>File No: <b style={{ color: '#0f172a' }}>{stdCertData[modalType][0]?.file_no || 'N/A'}</b></p>
              <p style={{ marginBottom: '10px' }}>Expiry Date: {stdCertData[modalType][0]?.expiry_date || 'N/A'}</p>
              <p style={{ marginBottom: '0px' }}>
                Status: <b style={{ color: stdCertData[modalType][0]?.expiry_status?.toLowerCase().includes('no action') || stdCertData[modalType][0]?.expiry_status?.toLowerCase().includes('active') ? '#16a34a' : '#dc2626' }}>
                  {stdCertData[modalType][0]?.expiry_status || 'N/A'}
                </b>
              </p>
            </>
          ) : (
            <p style={{ marginBottom: '0px', color: '#64748b' }}>No {modalType} information available in the system.</p>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default ConsolidatedReportView;
